import { Form, Input, message, Modal, Switch } from "antd";
import { Rule } from "antd/lib/form";
import { configurationApi } from "api/configuration.api";
import { AppButton } from "components/antd-ui";
import { InputNumber } from "components/Input/InputNumber";
import React, { useCallback, useImperativeHandle, useState } from "react";
import { Configuration } from "types/configuration";
import { configurationDataTypeLabel, configurationParamLabel } from "../configuration.meta";

const requiredRules: Rule[] = [{ required: true, message: "Bắt buộc" }];

interface ConfigurationForm {
  value: string;
}

export interface ConfigurationModal {
  handleUpdate: (configuration: Configuration) => void;
}

interface ConfigurationModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

export const ConfigurationModal = React.forwardRef(({ onClose, onSubmitOk }: ConfigurationModalProps, ref) => {
  const [form] = Form.useForm<ConfigurationForm>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [configuration, setConfiguration] = useState<Configuration>();

  const handleClose = useCallback(() => {
    setVisible(false);
    onClose();
  }, [onClose]);

  useImperativeHandle<any, ConfigurationModal>(
    ref,
    () => ({
      handleUpdate(next: Configuration) {
        form.setFieldsValue({ value: next.value ?? "" });
        setConfiguration(next);
        setVisible(true);
      },
    }),
    [form],
  );

  const handleSubmit = useCallback(async () => {
    if (!configuration) {
      return;
    }

    await form.validateFields();
    const value = String(form.getFieldValue("value") ?? "");
    setLoading(true);
    try {
      await configurationApi.update(configuration.id, { value });
      message.success("Cập nhật thành công!");
      handleClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  }, [configuration, form, handleClose, onSubmitOk]);

  const dataType = configuration?.dataType;

  return (
    <Modal
      onCancel={handleClose}
      open={visible}
      title="Cập nhật cấu hình"
      style={{ top: 20 }}
      width={520}
      confirmLoading={loading}
      footer={
        <div className="flex justify-end gap-2">
          <AppButton onClick={handleClose}>Hủy</AppButton>
          <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
            Lưu
          </AppButton>
        </div>
      }
    >
      <Form layout="vertical" form={form}>
        <Form.Item label="Tham số">
          <Input
            disabled
            value={
              configuration
                ? configurationParamLabel[configuration.param] || configuration.param
                : ""
            }
          />
        </Form.Item>
        <Form.Item label="Kiểu dữ liệu">
          <Input
            disabled
            value={configuration ? configurationDataTypeLabel[dataType || ""] || dataType : ""}
          />
        </Form.Item>
        {dataType === "BOOLEAN" ? (
          <Form.Item
            label="Giá trị"
            name="value"
            valuePropName="checked"
            getValueProps={(value) => ({ checked: value === "true" })}
            normalize={(checked: boolean) => (checked ? "true" : "false")}
          >
            <Switch />
          </Form.Item>
        ) : dataType === "NUMBER" ? (
          <Form.Item label="Giá trị" name="value" rules={requiredRules}>
            <InputNumber />
          </Form.Item>
        ) : (
          <Form.Item label="Giá trị" name="value" rules={requiredRules}>
            <Input />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
});
